import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import NavigationSidebar from '../../components/navigation/NavigationSidebar';
import UserProfileDropdown from '../../components/navigation/UserProfileDropdown';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import MetricCard from './components/MetricCard';
import ActivityFeedItem from './components/ActivityFeedItem';
import GroupCard from './components/GroupCard';
import VisaStatusCard from './components/VisaStatusCard';
import RevenueChart from './components/RevenueChart';
import CriticalAlertCard from './components/CriticalAlertCard';
import QuickActionButton from './components/QuickActionButton';
import Icon from '../../components/AppIcon';
import { getDashboardStats, getNotifications } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';

const AgencyDashboard = () => {
  const navigate = useNavigate();
  const { t, currentLanguage, setLanguage } = useLanguage();
  const { profile, loading: authLoading } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [dashboardStats, notificationsData] = await Promise.all([
      getDashboardStats(),
      getNotifications()]
      );
      setStats(dashboardStats);
      setNotifications(notificationsData || []);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const mockUser = profile || {
    name: "Ahmed Benali",
    role: "agency_admin",
    agency: "Voyages Omra Plus",
    avatar: "https://randomuser.me/api/portraits/men/45.jpg"
  };

  const mockNotifications = notifications;

  const mockMetrics = [
  {
    title: t('dashboard.activePilgrimsMetric'),
    value: stats?.activePilgrims?.toString() || '0',
    change: '+12.5%',
    changeType: 'positive',
    icon: 'Users',
    iconColor: 'text-primary',
    iconBgColor: 'bg-primary/10',
    trend: 'up'
  },
  {
    title: t('dashboard.pendingVisasMetric'),
    value: stats?.pendingVisas?.toString() || '0',
    change: `-5 ${t('dashboard.weekChange')}`,
    changeType: 'positive',
    icon: 'FileText',
    iconColor: 'text-warning',
    iconBgColor: 'bg-warning/10',
    trend: 'down'
  },
  {
    title: t('dashboard.upcomingDepartures'),
    value: stats?.activeGroups?.toString() || '0',
    change: `15 ${t('dashboard.daysAway')}`,
    changeType: 'neutral',
    icon: 'Plane',
    iconColor: 'text-success',
    iconBgColor: 'bg-success/10',
    trend: 'up'
  },
  {
    title: t('dashboard.revenueThisMonth'),
    value: `${((stats?.totalRevenue || 0) / 1000000)?.toFixed(1)}M MAD`,
    change: '+18.2%',
    changeType: 'positive',
    icon: 'DollarSign',
    iconColor: 'text-secondary',
    iconBgColor: 'bg-secondary/10',
    trend: 'up'
  }];


  const mockActivities = [
  {
    id: 1,
    title: t('dashboard.newPilgrimRegistered'),
    description: t('dashboard.newPilgrimDesc'),
    timestamp: new Date(Date.now() - 600000),
    priority: "normal",
    actionLabel: t('dashboard.viewProfile')
  },
  {
    id: 2,
    title: t('dashboard.missingDocumentAction'),
    description: t('dashboard.missingDocDesc'),
    timestamp: new Date(Date.now() - 1200000),
    priority: "high",
    actionLabel: t('dashboard.contactPilgrim')
  },
  {
    id: 3,
    title: t('dashboard.visaApproved'),
    description: t('dashboard.visaApprovedDesc'),
    timestamp: new Date(Date.now() - 2400000),
    priority: "low",
    actionLabel: t('dashboard.downloadVisa')
  },
  {
    id: 4,
    title: t('dashboard.paymentReceived'),
    description: t('dashboard.paymentReceivedDesc'),
    timestamp: new Date(Date.now() - 3600000),
    priority: "normal",
    actionLabel: t('dashboard.viewInvoice')
  }];


  const mockGroups = [
  {
    id: 1,
    name: "Omra Ramadan 2026",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ae2b6aad-1777040840486.png",
    imageAlt: "Aerial view of Masjid al-Haram in Makkah with thousands of pilgrims performing Tawaf around the Kaaba during sunset with golden light illuminating the Grand Mosque",
    destination: "Makkah - Madinah",
    departureDate: "2026-03-15",
    currentPilgrims: 45,
    totalCapacity: 50,
    supervisor: "Ahmed Tazi",
    status: "active"
  },
  {
    id: 2,
    name: "Hajj 2026 - Groupe A",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_143fe1149-1764821400415.png",
    imageAlt: "Wide panoramic view of Mount Arafat with white tents spread across the plains and thousands of pilgrims gathering for the Day of Arafah during Hajj season",
    destination: "Makkah - Mina - Arafat",
    departureDate: "2026-06-10",
    currentPilgrims: 38,
    totalCapacity: 60,
    supervisor: "Fatima Bennani",
    status: "active"
  },
  {
    id: 3,
    name: "Omra Février 2026",
    image: "https://images.unsplash.com/photo-1703179094691-6612cb65f356",
    imageAlt: "Night view of the illuminated Prophet's Mosque in Madinah with its distinctive green dome and tall minarets surrounded by pilgrims in the courtyard",
    destination: "Madinah - Makkah",
    departureDate: "2026-02-20",
    currentPilgrims: 50,
    totalCapacity: 50,
    supervisor: "Hassan Alaoui",
    status: "completed"
  }];


  const mockVisaStatuses = [
  {
    type: "pending",
    count: 18,
    description: t('dashboard.pendingApproval')
  },
  {
    type: "processing",
    count: 12,
    description: t('dashboard.inProcessing')
  },
  {
    type: "approved",
    count: 156,
    description: t('dashboard.approvedThisMonth')
  },
  {
    type: "rejected",
    count: 3,
    description: t('dashboard.needsReview')
  }];


  const mockRevenueData = [
  { month: currentLanguage === 'fr' ? 'Jan' : 'يناير', revenue: 850000 },
  { month: currentLanguage === 'fr' ? 'Fév' : 'فبراير', revenue: 920000 },
  { month: currentLanguage === 'fr' ? 'Mar' : 'مارس', revenue: 1150000 },
  { month: currentLanguage === 'fr' ? 'Avr' : 'أبريل', revenue: 980000 },
  { month: currentLanguage === 'fr' ? 'Mai' : 'مايو', revenue: 1050000 },
  { month: currentLanguage === 'fr' ? 'Juin' : 'يونيو', revenue: 1200000 }];


  const mockCriticalAlerts = [
  {
    id: 1,
    severity: "critical",
    title: currentLanguage === 'fr' ?
    "Documents manquants - Départ imminent" : "مستندات مفقودة - مغادرة وشيكة",
    description: currentLanguage === 'fr' ? "5 pèlerins du groupe Omra Mars 2026 n'ont pas soumis leurs passeports. Départ dans 15 jours." : "5 حجاج من مجموعة عمرة مارس 2026 لم يقدموا جوازات سفرهم. المغادرة في 15 يوماً.",
    actionLabel: t('dashboard.viewPilgrims'),
    secondaryActionLabel: t('dashboard.sendReminder')
  },
  {
    id: 2,
    severity: "warning",
    title: currentLanguage === 'fr' ?
    "Quota groupe presque atteint" : "حصة المجموعة على وشك الامتلاء",
    description: currentLanguage === 'fr' ?
    "Le groupe Hajj 2026 - Groupe A a atteint 95% de sa capacité. 3 places restantes." : "وصلت مجموعة حج 2026 - المجموعة أ إلى 95٪ من سعتها. 3 أماكن متبقية.",
    actionLabel: t('dashboard.manageGroup'),
    secondaryActionLabel: null
  }];


  const quickActions = [
  {
    icon: 'UserPlus',
    label: t('dashboard.newPilgrim'),
    color: 'primary',
    action: () => navigate('/pilgrim-management')
  },
  {
    icon: 'UsersRound',
    label: t('dashboard.createGroup'),
    color: 'success',
    action: () => navigate('/group-management')
  },
  {
    icon: 'FileText',
    label: t('dashboard.processVisa'),
    color: 'warning',
    action: () => navigate('/visa-management')
  },
  {
    icon: 'DollarSign',
    label: t('dashboard.finances'),
    color: 'secondary',
    action: () => navigate('/financial-management')
  }];


  const handleLogout = () => {
    navigate('/login');
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
  };

  const handleNotificationClick = (notification) => {
    console.log('Notification clicked:', notification);
  };

  const handleMarkAsRead = (notificationId) => {
    console.log('Mark as read:', notificationId);
  };

  const handleAlertAction = (alertId) => {
    console.log('Alert action:', alertId);
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationSidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />

      <div className={`transition-smooth ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'}`}>
        <header className="sticky top-0 z-30 bg-card border-b border-border shadow-warm">
          <div className="flex items-center justify-between pl-16 pr-4 md:px-6 lg:px-8 py-4">
            <div className="flex-1">
              <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
                {t('dashboard.agencyDashboard')}
              </h1>
              <p className="caption text-muted-foreground mt-1">
                {t('dashboard.dashboardOverview')}
              </p>
            </div>
            <div className="flex items-center gap-3 md:gap-4">
              <NotificationCenter
                notifications={mockNotifications}
                onNotificationClick={handleNotificationClick}
                onMarkAsRead={handleMarkAsRead}
                currentLanguage={currentLanguage} />

              <UserProfileDropdown
                user={mockUser}
                onLogout={handleLogout}
                onLanguageChange={handleLanguageChange}
                currentLanguage={currentLanguage} />

            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <BreadcrumbNavigation currentLanguage={currentLanguage} />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            {mockMetrics?.map((metric, index) =>
            <MetricCard key={index} {...metric} />
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-card rounded-lg p-4 md:p-6 shadow-warm border border-border">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h2 className="text-lg md:text-xl font-semibold text-foreground">
                    {t('dashboard.recentActivities')}
                  </h2>
                  <button className="caption text-primary hover:text-primary/80 font-medium transition-smooth">
                    {t('dashboard.viewAll')}
                  </button>
                </div>
                <div className="space-y-3 md:space-y-4">
                  {mockActivities?.map((activity) =>
                  <ActivityFeedItem
                    key={activity?.id}
                    activity={activity}
                    currentLanguage={currentLanguage} />

                  )}
                </div>
              </div>

              <div className="bg-card rounded-lg p-4 md:p-6 shadow-warm border border-border">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h2 className="text-lg md:text-xl font-semibold text-foreground">
                    {t('dashboard.activeGroups')}
                  </h2>
                  <button
                    onClick={() => navigate('/group-management')}
                    className="caption text-primary hover:text-primary/80 font-medium transition-smooth">

                    {t('dashboard.manageGroups')}
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  {mockGroups?.slice(0, 2)?.map((group) =>
                  <GroupCard
                    key={group?.id}
                    group={group}
                    currentLanguage={currentLanguage} />

                  )}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-card rounded-lg p-4 md:p-6 shadow-warm border border-border">
                <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4 md:mb-6">
                  {t('dashboard.quickActions')}
                </h2>
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  {quickActions?.map((action, index) =>
                  <QuickActionButton
                    key={index}
                    icon={action?.icon}
                    label={action?.label}
                    color={action?.color}
                    onClick={action?.action} />

                  )}
                </div>
              </div>

              <div className="bg-card rounded-lg p-4 md:p-6 shadow-warm border border-border">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h2 className="text-lg md:text-xl font-semibold text-foreground">
                    {t('dashboard.visaStatus')}
                  </h2>
                  <button
                    onClick={() => navigate('/visa-management')}
                    className="caption text-primary hover:text-primary/80 font-medium transition-smooth">

                    {t('dashboard.viewAll')}
                  </button>
                </div>
                <div className="space-y-3 md:space-y-4">
                  {mockVisaStatuses?.map((status, index) =>
                  <VisaStatusCard
                    key={index}
                    status={status}
                    currentLanguage={currentLanguage} />

                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
            <div className="lg:col-span-2">
              <RevenueChart
                data={mockRevenueData}
                currentLanguage={currentLanguage} />

            </div>

            <div className="space-y-4 md:space-y-6">
              <div className="bg-card rounded-lg p-4 md:p-6 shadow-warm border border-border">
                <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                  {t('dashboard.financialSummary')}
                </h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-border">
                    <span className="text-sm text-muted-foreground">
                      {t('dashboard.totalRevenue')}
                    </span>
                    <span className="text-base font-semibold text-foreground data-text">
                      6.15M MAD
                    </span>
                  </div>
                  <div className="flex items-center justify-between pb-3 border-b border-border">
                    <span className="text-sm text-muted-foreground">
                      {t('dashboard.pendingPayments')}
                    </span>
                    <span className="text-base font-semibold text-warning data-text">
                      450K MAD
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {t('dashboard.netProfit')}
                    </span>
                    <span className="text-base font-semibold text-success data-text">
                      1.85M MAD
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 md:space-y-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertTriangle" size={24} className="text-error" />
              <h2 className="text-lg md:text-xl font-semibold text-foreground">
                {t('dashboard.criticalAlerts')}
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              {mockCriticalAlerts?.map((alert) =>
              <CriticalAlertCard
                key={alert?.id}
                alert={alert}
                currentLanguage={currentLanguage}
                onAction={handleAlertAction} />

              )}
            </div>
          </div>
        </main>
      </div>
    </div>);

};

export default AgencyDashboard;